/**
 * Claims `/admin` and everything under it, WITHOUT pulling the console into the
 * shell bundle.
 *
 * `main.ts` imports this module for its side effect and nothing else. It is
 * deliberately tiny — a registry call and a dynamic import — so the shell keeps
 * paying only for the knowledge that the route exists, not for eleven admin
 * pages the overwhelming majority of visitors will never open. The console
 * itself arrives through `await import('.')`, which is what makes it its own
 * chunk (ADR-0005, and the guard in `vite.config.ts` measures the result).
 *
 * ── WHY IT DOES NOT IMPORT `navigate` FROM `main.ts` ────────────────────────
 * `main.ts` imports this file, so importing back would close a cycle. It also
 * would not work: `main.ts`'s `navigate` compares only `location.pathname`, so
 * moving from `/admin/users` to `/admin/users?id=…` — the console's own
 * list-to-detail step — would push nothing and re-render the list. Pushing the
 * full URL here and firing `popstate` uses the same listener `main.ts` already
 * has for the back button, with no new coupling in either direction.
 */
import { t } from '../i18n';
import { registerScreen, ROUTES } from '../layout';
import type { AuthUser } from '@/core/state/auth-store';
import { authStore } from '@/core/state/auth-store';

/** Registers the console. Idempotent — a second call replaces the same entries. */
export function registerAdminScreens(): void {
  registerScreen('/admin', renderAdminArea);
  registerScreen('/admin/*', renderAdminArea);
}

registerAdminScreens();

function renderAdminArea(root: HTMLElement): () => void {
  let dispose: (() => void) | null = null;
  let cancelled = false;

  // The user is read here, not inside the console, so a signed-out visitor
  // typing /admin gets the gate without the chunk being downloaded first.
  let user: AuthUser | null = authStore.get().user;

  // A session that ends mid-visit — an expired refresh token, a sign-out in
  // another tab — must take the console down with it. `main.ts` only routes a
  // session-less user away from `/play`, so without this an operator would sit
  // in a fully drawn console where every button 401s.
  let lastUserId = user?.id ?? null;
  const unsubscribe = authStore.subscribe((state) => {
    const nextId = state.user?.id ?? null;
    if (nextId === lastUserId) return;
    lastUserId = nextId;
    user = state.user;
    dispose?.();
    dispose = null;
    mount();
  });

  mount();

  function mount(): void {
    void import('./index')
      .then(({ mountAdmin }) => {
        if (cancelled) return;
        dispose = mountAdmin(root, {
          user,
          navigate: go,
          onExit: () => go(user === null ? ROUTES.login : ROUTES.play),
        });
      })
      .catch((error: unknown) => {
        // A failed chunk fetch must SAY so. Rendering nothing leaves a blank
        // page that reads like a permissions problem, and the operator retries
        // the wrong thing. The message comes from the shared catalogue rather
        // than the console's own, because the console is exactly what failed to
        // arrive — `errors.network` is the accurate one: a dynamic import that
        // rejects is a failed fetch of the chunk.
        console.error('[admin] the operator console failed to load', error);
        root.textContent = '';
        const message = document.createElement('p');
        message.className = 'p-6 text-sm';
        message.setAttribute('role', 'alert');
        message.textContent = t('errors.network');
        root.appendChild(message);
      });
  }

  return () => {
    cancelled = true;
    unsubscribe();
    dispose?.();
    dispose = null;
  };
}

/**
 * Navigate within the SPA, preserving the query string.
 *
 * `popstate` is the event `main.ts` already re-renders on, so this needs no
 * export from, or import of, the entry module.
 */
function go(to: string): void {
  if (location.pathname + location.search !== to) history.pushState(null, '', to);
  globalThis.dispatchEvent(new PopStateEvent('popstate'));
}
